import { voiceSpeaksLanguage } from './voiceLanguageDefaults';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';
import type { Language } from '@src/types.ts/language';

export interface VoiceLanguageSummary {
  /** One short line for a voice row, workspace languages first. */
  text: string;
  /** Every language the voice speaks, comma-separated, for a tooltip. */
  full: string;
  /** The same languages as captions, workspace ones first, for a stacked list. */
  names: string[];
  /** Whether the voice speaks at least one workspace language. */
  coversWorkspace: boolean;
}

// What a voice speaks, as a row can afford to show it: the workspace's
// languages first (they are what the priority order is resolved against),
// capped at `maxNamed`, with everything else folded into "+N". Uses the same
// speaks-this-language rule as the per-language defaults so the row and the
// Language Voice Defaults tab never disagree (PLAT-11575).
export function voiceLanguageSummary(
  voice: AiVoiceRecord,
  workspaceLanguages: Language[],
  maxNamed = 2,
): VoiceLanguageSummary {
  const inWorkspace = workspaceLanguages.filter(l =>
    voiceSpeaksLanguage(voice, l),
  );
  const workspaceIds = new Set(inWorkspace.map(l => l.id));
  const others = (voice.supportedLanguages ?? []).filter(
    l => !workspaceIds.has(l.id),
  );
  const names = [...inWorkspace, ...others].map(l => l.caption);
  const full = names.join(', ');

  if (inWorkspace.length === 0) {
    const text =
      others.length === 0
        ? 'No languages listed'
        : `No workspace languages · ${others.length} other${others.length === 1 ? '' : 's'}`;
    return { text, full, names, coversWorkspace: false };
  }

  const named = inWorkspace
    .slice(0, maxNamed)
    .map(l => l.caption)
    .join(', ');
  const rest = Math.max(0, inWorkspace.length - maxNamed) + others.length;
  return {
    text: rest > 0 ? `${named} +${rest}` : named,
    full,
    names,
    coversWorkspace: true,
  };
}
